import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { Box } from '@mui/material';
import AppBar from './components/AppBar';
import HomePage from './components/HomePage';
import ItemsList from './components/ItemsList';
import ItemPage from './components/ItemPage';
import LoginPage from './components/LoginPage';
import RegisterPage from './components/RegisterPage';
import VerifyEmailPage from './components/VerifyEmailPage';
import RP from './components/RP'; // Импорт компонента для восстановления пароля
import Profile from './components/Profile'; // Импорт профиля
import Terms from './components/terms';
import OrdersPage from './components/OrdersPage';
import CartPage from './components/CartPage';
import ServicesPage from './components/ServicesPage';
import ServicePage from './components/ServicePage';
import ProductsPage from './components/ProductsPage';
import ProductPage from './components/ProductPage';
import ToursPage from './components/ToursPage';
import TourPage from './components/TourPage';
import TourBookingPage from './components/TourBookingPage';
import SearchPage from './components/SearchPage';
import BonusPage from './components/BonusPage';
import PartnersPage from './components/PartnersPage';
import WelcomeDialog from './components/WelcomeDialog';
import FavoritesPage from './components/FavoritesPage';
import { FavoritesProvider } from './context/FavoritesContext';
import SsoHandoffPage from './components/SsoHandoffPage';
import SsoCallbackPage from './components/SsoCallbackPage';
import { isSsoRoute } from './utils/ssoAuth';

function AppContent() {
  const [user, setUser] = useState(null);
  const [welcomeDialogOpen, setWelcomeDialogOpen] = useState(false);
  const location = useLocation();

  // Функция для загрузки данных профиля пользователя
  const fetchUserProfile = async () => {
    console.log('Fetching user profile...');
    const token = localStorage.getItem('token');
    if (!token) return;

    try {
      const response = await fetch(`${process.env.REACT_APP_SERVER_URL}/api/profile`, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) throw new Error('Ошибка авторизации');
      const userData = await response.json();
      console.log('User profile fetched:', userData); // Лог для проверки данных
      setUser(userData);
    } catch (error) {
      console.error('Ошибка проверки токена:', error.message);
      localStorage.removeItem('token');
      setUser(null);
    }
  };

  useEffect(() => {
    if (!isSsoRoute(location.pathname)) {
      fetchUserProfile();
    }
  }, []);

  // Приветственное модальное окно — только при первом запуске на главной
  useEffect(() => {
    if (location.pathname !== '/') {
      setWelcomeDialogOpen(false);
      return;
    }
    const seenInStorage = localStorage.getItem('welcomeModalSeen') === 'true';
    const seenInProfile = user?.dontShowFilterModal === true;
    setWelcomeDialogOpen(!seenInStorage && !seenInProfile);
  }, [location.pathname, user?.dontShowFilterModal]);

  // Функция выхода из аккаунта
  const logout = () => {
    localStorage.removeItem('token');
    setUser(null);
  };

  const handleWelcomeDialogClose = () => {
    setWelcomeDialogOpen(false);
  };

  return (
    <FavoritesProvider user={user}>
      <AppBar user={user} logout={logout} onProfileUpdate={setUser} />
      <Box sx={{ pb: { xs: 6, md: 0 } }}> {/* Отступ снизу под уменьшенное нижнее меню (48px) */}
        <Routes>
          <Route path="/sso/handoff" element={<SsoHandoffPage />} />
          <Route path="/sso/callback" element={<SsoCallbackPage onSuccess={fetchUserProfile} />} />
          <Route path="/" element={<HomePage user={user} />} />
          <Route path="/rental" element={<ItemsList user={user} />} />
          <Route path="/item/:id" element={<ItemPage />} />
          <Route
            path="/login"
            element={
              <LoginPage
                onLoginSuccess={() => {
                  fetchUserProfile(); // Загружаем профиль после успешного входа
                }}
              />
            }
          />
          <Route
            path="/register"
            element={
              <RegisterPage
                onRegisterSuccess={() => {
                  fetchUserProfile(); // Загружаем профиль после успешной регистрации
                }}
              />
            }
          />
          <Route path="/verify-email/:token" element={<VerifyEmailPage setUser={setUser} />} />
          <Route path="/profile/settings" element={<Navigate to="/profile" replace />} />
          <Route
            path="/profile"
            element={
              localStorage.getItem('token') ? (
                <Profile user={user} setUser={setUser} />
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route path="/orders" element={<OrdersPage />} /> {/* Страница заказов */}
          <Route path="/bonus" element={<BonusPage />} />
          <Route path="/cart" element={<CartPage />} /> {/* Страница корзины */}
          <Route
            path="/favorites"
            element={
              localStorage.getItem('token') ? (
                <FavoritesPage />
              ) : (
                <Navigate to="/login" replace state={{ redirectTo: '/favorites' }} />
              )
            }
          />
          <Route path="/services" element={<ServicesPage />} /> {/* Страница услуг */}
          <Route path="/services/:id" element={<ServicePage />} /> {/* Детальная страница услуги */}
          <Route path="/products" element={<ProductsPage user={user} />} /> {/* Товары (продажа) */}
          <Route path="/products/:productNumber" element={<ProductPage />} /> {/* Карточка товара */}
          <Route path="/tours" element={<ToursPage />} />
          <Route path="/tours/:tourNumber/book" element={<TourBookingPage />} />
          <Route path="/tours/:tourNumber" element={<TourPage />} />
          <Route path="/search" element={<SearchPage />} />
          <Route path="/partners" element={<PartnersPage />} />
          <Route path="/place-service" element={<Navigate to="/partners#service" replace />} />
          <Route path="/rent-out" element={<Navigate to="/partners#equipment" replace />} />
          <Route path="/reset-password/:token" element={<RP />} /> {/* Новый маршрут */}
          <Route path="/terms" element={<Terms />} />
        </Routes>
      </Box>
      <WelcomeDialog open={welcomeDialogOpen} onClose={handleWelcomeDialogClose} user={user} onPreferenceSaved={(updatedUser) => updatedUser && setUser(updatedUser)} />
    </FavoritesProvider>
  );
}

function App() {
  return (
    <Router>
      <AppContent />
    </Router>
  );
}

export default App;
